import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  Stack,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import { useAxiosClient } from '../utils/useAxiosClient';

interface UsageUser {
  userId: number;
  userEmail: string;
  events: number;
  sessions: number;
  pageviews: number;
  clicks: number;
  totalMinutes: number;
  firstSeen: string | null;
  lastSeen: string | null;
}

interface UsagePath {
  path: string;
  views: number;
  users: number;
  totalMinutes: number;
}

interface UsageSummary {
  days: number;
  users: UsageUser[];
  topPaths: UsagePath[];
}

const DAY_OPTIONS = [7, 30, 90];

const fmtDateTime = (iso: string | null): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const fmtMinutes = (m: number): string => {
  if (!m) return '0m';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${Math.round(m % 60)}m`;
};

/**
 * Site usage for one organization (or all orgs when organizationId is null).
 *
 * Shared by the org detail page and the cross-org Dashboard Logs page so both
 * read the same numbers from the same endpoint.
 *
 * Only events carrying an organization_id can be attributed to an org, and only
 * org-scoped routes (?org=<id> or /admin/org/<id>) carry one. Admin pages that
 * aren't org-scoped show up under "All organizations" but not under a specific
 * org -- the caption below says so rather than leaving a confusing gap.
 */
const OrgUsagePanel: React.FC<{ organizationId?: number | null; title?: string }> = ({
  organizationId = null,
  title,
}) => {
  const axiosClient = useAxiosClient();
  const [days, setDays] = useState(30);
  const [trialsOnly, setTrialsOnly] = useState(false);
  const [data, setData] = useState<UsageSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, any> = { days, trials_only: trialsOnly };
      if (organizationId != null) params.organization_id = organizationId;
      const res = await axiosClient.get('/api/admin/analytics/summary', { params });
      setData(res.data);
    } catch (e: any) {
      setError(e?.response?.data?.detail?.error || e?.response?.data?.error || 'Failed to load usage');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [axiosClient, organizationId, days, trialsOnly]);

  useEffect(() => { void load(); }, [load]);

  const totals = data?.users.reduce(
    (acc, u) => ({
      users: acc.users + 1,
      sessions: acc.sessions + u.sessions,
      pageviews: acc.pageviews + u.pageviews,
      minutes: acc.minutes + u.totalMinutes,
    }),
    { users: 0, sessions: 0, pageviews: 0, minutes: 0 }
  ) || { users: 0, sessions: 0, pageviews: 0, minutes: 0 };

  const maxViews = Math.max(1, ...(data?.topPaths.map(p => p.views) || [1]));
  const cellSx = { fontSize: '0.85rem' };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Typography variant="h5">{title || 'Usage'}</Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControlLabel
            control={<Switch size="small" checked={trialsOnly} onChange={(e) => setTrialsOnly(e.target.checked)} />}
            label={<Typography variant="body2">Trial users only</Typography>}
          />
          <ToggleButtonGroup
            size="small"
            exclusive
            value={days}
            onChange={(_, v) => v && setDays(v)}
          >
            {DAY_OPTIONS.map(d => (
              <ToggleButton key={d} value={d}>{d}d</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
      )}

      {!loading && data && data.users.length === 0 && (
        <Paper sx={{ p: 5, textAlign: 'center', bgcolor: 'action.hover' }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            No activity recorded
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Nothing in the last {days} days
            {trialsOnly ? ' from trial users' : ''}. Usage tracking only records from the point it
            was deployed, so earlier visits are not counted.
          </Typography>
        </Paper>
      )}

      {!loading && data && data.users.length > 0 && (
        <Stack spacing={3}>
          <Stack direction="row" spacing={4} flexWrap="wrap">
            {[
              ['Active users', totals.users],
              ['Sessions', totals.sessions],
              ['Pageviews', totals.pageviews],
              ['Time on site', fmtMinutes(Math.round(totals.minutes))],
            ].map(([label, value]) => (
              <Box key={String(label)}>
                <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                <Typography variant="h5">{value}</Typography>
              </Box>
            ))}
          </Stack>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>By user</Typography>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...cellSx, fontWeight: 600 }}>Email</TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: 600 }} align="right">Sessions</TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: 600 }} align="right">Pageviews</TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: 600 }} align="right">Clicks</TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: 600 }} align="right">Time</TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: 600 }}>First seen</TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: 600 }}>Last seen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.users.map(u => (
                    <TableRow key={u.userId} hover>
                      <TableCell sx={{ ...cellSx, fontWeight: 500 }}>{u.userEmail}</TableCell>
                      <TableCell sx={cellSx} align="right">{u.sessions}</TableCell>
                      <TableCell sx={cellSx} align="right">{u.pageviews}</TableCell>
                      <TableCell sx={cellSx} align="right">{u.clicks}</TableCell>
                      <TableCell sx={cellSx} align="right">{fmtMinutes(u.totalMinutes)}</TableCell>
                      <TableCell sx={cellSx}>{fmtDateTime(u.firstSeen)}</TableCell>
                      <TableCell sx={cellSx}>{fmtDateTime(u.lastSeen)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Most-visited pages</Typography>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...cellSx, fontWeight: 600 }}>Path</TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: 600, width: '30%' }}>Views</TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: 600 }} align="right">Users</TableCell>
                    <TableCell sx={{ ...cellSx, fontWeight: 600 }} align="right">Time</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.topPaths.map(p => (
                    <TableRow key={p.path} hover>
                      <TableCell sx={cellSx}>
                        <Tooltip arrow title={p.path || ''}>
                          <span>{p.path}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={cellSx}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={(p.views / maxViews) * 100}
                            sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
                          />
                          <Typography variant="caption" sx={{ minWidth: 28, textAlign: 'right' }}>{p.views}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={cellSx} align="right">{p.users}</TableCell>
                      <TableCell sx={cellSx} align="right">{fmtMinutes(p.totalMinutes)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Stack>
      )}

      {organizationId != null && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Only org-scoped routes carry an organization, so admin pages outside this org are not
          counted here. See Dashboard Logs for activity across all organizations.
        </Typography>
      )}
    </Box>
  );
};

export default OrgUsagePanel;
